export const META_DESCRIPTION_MAX_LEN = 9999;

const ALL_CAPS_LETTER_MIN = 4;
const ALL_CAPS_RATIO = 0.8;

const HTML_TAG_RE = /<\/?[a-z][a-z0-9]*\b[^>]*>/i;
const URL_HTTP_RE = /https?:\/\//i;
const URL_WWW_RE = /\bwww\.\S+/i;
/** Conservative bare-domain hint (catalog plain-text rule). */
const URL_BARE_RE = /\b[a-z0-9][-a-z0-9]*\.(?:com|net|org|co|io|us|uk)\b/i;

export type MetaDescriptionWarningCode =
  | 'empty'
  | 'all_caps'
  | 'contains_url'
  | 'same_as_title'
  | 'contains_html';

export type MetaDescriptionErrorCode = 'too_long';

export type MetaDescriptionWarning = { code: MetaDescriptionWarningCode; message: string };
export type MetaDescriptionError = { code: MetaDescriptionErrorCode; message: string };

export type MetaDescriptionValidationResult = {
  warnings: MetaDescriptionWarning[];
  hardErrors: MetaDescriptionError[];
};

function letterUppercaseRatio(s: string): { letters: number; upper: number } {
  let letters = 0;
  let upper = 0;
  for (const ch of s) {
    if (!/[a-zA-Z]/.test(ch)) continue;
    letters += 1;
    if (ch === ch.toUpperCase() && ch !== ch.toLowerCase()) upper += 1;
  }
  return { letters, upper };
}

/**
 * Validates a catalog `description` against Meta-oriented rules.
 * @param text — raw description (may include whitespace)
 * @param inventoryTitle — CruiseControl title `{year make model trim}` for same-as-title check
 */
export function validateMetaDescription(
  text: string,
  inventoryTitle: string,
): MetaDescriptionValidationResult {
  const trimmed = text.trim();
  const warnings: MetaDescriptionWarning[] = [];
  const hardErrors: MetaDescriptionError[] = [];

  if (trimmed.length > META_DESCRIPTION_MAX_LEN) {
    hardErrors.push({
      code: 'too_long',
      message: `Description cannot exceed ${META_DESCRIPTION_MAX_LEN.toLocaleString('en-US')} characters.`,
    });
  }

  if (trimmed.length === 0) {
    warnings.push({
      code: 'empty',
      message: '',
    });
    return { warnings, hardErrors };
  }

  const { letters, upper } = letterUppercaseRatio(trimmed);
  if (letters >= ALL_CAPS_LETTER_MIN && upper / letters >= ALL_CAPS_RATIO) {
    warnings.push({
      code: 'all_caps',
      message:
        'Most letters are uppercase. Catalog feeds work better with sentence case.',
    });
  }

  if (HTML_TAG_RE.test(trimmed)) {
    warnings.push({
      code: 'contains_html',
      message: 'Remove HTML tags; this field is plain text for catalog feeds.',
    });
  }

  if (URL_HTTP_RE.test(trimmed) || URL_WWW_RE.test(trimmed) || URL_BARE_RE.test(trimmed)) {
    warnings.push({
      code: 'contains_url',
      message: 'Remove web addresses from this field; use plain text for catalog feeds.',
    });
  }

  const t = inventoryTitle.trim();
  if (t.length > 0 && trimmed.toLowerCase() === t.toLowerCase()) {
    warnings.push({
      code: 'same_as_title',
      message: 'Use a description that differs from the vehicle title for catalog quality.',
    });
  }

  return { warnings, hardErrors };
}

/** Truncate for catalog emission (defensive if DB ever holds longer text). */
export function truncateMetaDescription(text: string, maxLen = META_DESCRIPTION_MAX_LEN): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen);
}
